import {Outlet, NavLink, useNavigate} from 'react-router-dom'
import {useAuth} from '../../app/modules/auth'
import {PageDataProvider} from './core'

// Ordered into logical groups: overview → people → catalog → money → comms.
// Every item has a distinct icon.
const NAV_ITEMS = [
  // ── Overview ──
  {
    to: '/dashboard',
    label: 'Dashboard',
    icon: (
      <svg width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
        <rect x='3' y='3' width='7' height='7'/><rect x='14' y='3' width='7' height='7'/>
        <rect x='3' y='14' width='7' height='7'/><rect x='14' y='14' width='7' height='7'/>
      </svg>
    ),
  },
  {
    to: '/analytics',
    label: 'Analytics',
    icon: (
      <svg width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
        <line x1='18' y1='20' x2='18' y2='10'/><line x1='12' y1='20' x2='12' y2='4'/><line x1='6' y1='20' x2='6' y2='14'/>
      </svg>
    ),
  },
  {
    to: '/activity',
    label: 'Live Activity',
    icon: (
      <svg width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
        <polyline points='13 2 3 14 12 14 11 22 21 10 12 10 13 2'/>
      </svg>
    ),
  },
  // ── People ──
  {
    to: '/users',
    label: 'Users',
    icon: (
      <svg width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
        <path d='M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2'/>
        <circle cx='12' cy='7' r='4'/>
      </svg>
    ),
  },
  {
    to: '/providers',
    label: 'Service Providers',
    icon: (
      <svg width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
        <rect x='2' y='7' width='20' height='14' rx='2'/><path d='M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2'/>
      </svg>
    ),
  },
  {
    to: '/provider-performance',
    label: 'Provider Performance',
    icon: (
      <svg width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
        <path d='M16 11l2 2 4-4'/><path d='M20 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2'/><circle cx='9' cy='7' r='4'/>
      </svg>
    ),
  },
  // ── Catalog ──
  {
    to: '/events',
    label: 'Events',
    icon: (
      <svg width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
        <rect x='3' y='4' width='18' height='18' rx='2'/><line x1='16' y1='2' x2='16' y2='6'/>
        <line x1='8' y1='2' x2='8' y2='6'/><line x1='3' y1='10' x2='21' y2='10'/>
      </svg>
    ),
  },
  {
    to: '/bookings',
    label: 'Bookings',
    icon: (
      <svg width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
        <path d='M9 11l3 3L22 4'/><path d='M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11'/>
      </svg>
    ),
  },
  {
    to: '/service-categories',
    label: 'Service Categories',
    icon: (
      <svg width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
        <polygon points='12 2 2 7 12 12 22 7 12 2'/><polyline points='2 17 12 22 22 17'/><polyline points='2 12 12 17 22 12'/>
      </svg>
    ),
  },
  {
    to: '/event-types',
    label: 'Event Types',
    icon: (
      <svg width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
        <path d='M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z'/>
        <line x1='7' y1='7' x2='7.01' y2='7'/>
      </svg>
    ),
  },
  // ── Money ──
  {
    to: '/payments-disputes',
    label: 'Payments & Disputes',
    icon: (
      <svg width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
        <rect x='1' y='4' width='22' height='16' rx='2'/><line x1='1' y1='10' x2='23' y2='10'/>
      </svg>
    ),
  },
  {
    to: '/payouts',
    label: 'Payouts',
    icon: (
      <svg width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
        <line x1='12' y1='1' x2='12' y2='23'/>
        <path d='M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6'/>
      </svg>
    ),
  },
  {
    to: '/coupons',
    label: 'Coupons',
    icon: (
      <svg width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
        <path d='M9 9h.01M15 15h.01M16 8l-8 8'/>
        <path d='M3 7a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v2a2 2 0 0 0 0 4v2a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-2a2 2 0 0 0 0-4z'/>
      </svg>
    ),
  },
  {
    to: '/redeem-codes',
    label: 'Redeem Codes',
    icon: (
      <svg width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
        <polyline points='20 12 20 22 4 22 4 12'/><rect x='2' y='7' width='20' height='5'/>
        <line x1='12' y1='22' x2='12' y2='7'/><path d='M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z'/>
        <path d='M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z'/>
      </svg>
    ),
  },
  // ── Comms ──
  {
    to: '/broadcast',
    label: 'Notifications',
    icon: (
      <svg width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
        <path d='M3 11l18-5v12L3 14v-3z'/><path d='M11.6 16.8a3 3 0 1 1-5.8-1.6'/>
      </svg>
    ),
  },
  {
    to: '/legal-documents',
    label: 'Legal Documents',
    icon: (
      <svg width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
        <path d='M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z'/>
        <polyline points='14 2 14 8 20 8'/><line x1='16' y1='13' x2='8' y2='13'/>
        <line x1='16' y1='17' x2='8' y2='17'/><line x1='10' y1='9' x2='8' y2='9'/>
      </svg>
    ),
  },
  {
    to: '/account/settings',
    label: 'Settings',
    icon: (
      <svg width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
        <circle cx='12' cy='12' r='3'/>
        <path d='M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z'/>
      </svg>
    ),
  },
]

const MasterLayout = () => {
  const {currentUser, logout} = useAuth()
  const navigate = useNavigate()

  const initials =
    ((currentUser?.first_name?.[0] ?? '') + (currentUser?.last_name?.[0] ?? '')).toUpperCase() || 'AD'

  const handleLogout = () => {
    logout()
    navigate('/auth/login')
  }

  return (
    <PageDataProvider>
      <div style={{display: 'flex', height: '100vh', background: '#f8fafc', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'}}>

        {/* ── Sidebar ── */}
        <aside style={{
          width: 260,
          flexShrink: 0,
          background: '#ffffff',
          borderRight: '1px solid #e2e8f0',
          display: 'flex',
          flexDirection: 'column',
          height: '100vh',
          position: 'fixed',
          left: 0,
          top: 0,
          zIndex: 100,
          overflowY: 'auto',
        }}>
          {/* Logo */}
          <div style={{padding: '24px 20px 20px', borderBottom: '1px solid #f1f5f9'}}>
            <div style={{display: 'flex', alignItems: 'center', gap: 10}}>
              <img
                src='/app-icon.png'
                alt='Logo'
                style={{width: 36, height: 36, borderRadius: 8, objectFit: 'cover'}}
              />
              <span style={{fontSize: 16, fontWeight: 700, color: '#1e293b'}}>Admin Panel</span>
            </div>
          </div>

          {/* Nav items */}
          <nav style={{flex: 1, padding: '12px 12px'}}>
            {NAV_ITEMS.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                style={({isActive}) => ({
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '9px 12px',
                  borderRadius: 8,
                  marginBottom: 2,
                  fontSize: 14,
                  fontWeight: isActive ? 600 : 400,
                  color: isActive ? '#3b82f6' : '#64748b',
                  background: isActive ? '#eff6ff' : 'transparent',
                  textDecoration: 'none',
                  transition: 'all 0.15s',
                })}
              >
                {item.icon}
                {item.label}
              </NavLink>
            ))}
          </nav>

          {/* Logout */}
          <div style={{padding: '12px 12px', borderTop: '1px solid #f1f5f9'}}>
            <button
              onClick={handleLogout}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '9px 12px',
                borderRadius: 8,
                width: '100%',
                fontSize: 14,
                fontWeight: 400,
                color: '#ef4444',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                textAlign: 'left',
              }}
            >
              <svg width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
                <path d='M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4'/>
                <polyline points='16 17 21 12 16 7'/>
                <line x1='21' y1='12' x2='9' y2='12'/>
              </svg>
              Logout
            </button>
          </div>
        </aside>

        {/* ── Main area ── */}
        <div style={{marginLeft: 260, flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0}}>

          {/* Topbar */}
          <header style={{
            height: 64,
            background: '#ffffff',
            borderBottom: '1px solid #e2e8f0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            padding: '0 24px',
            position: 'sticky',
            top: 0,
            zIndex: 50,
            flexShrink: 0,
          }}>
            <div style={{display: 'flex', alignItems: 'center', gap: 12}}>
              <div
                style={{
                  width: 38,
                  height: 38,
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #3b82f6, #6366f1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#fff',
                  fontWeight: 700,
                  fontSize: 13,
                  flexShrink: 0,
                }}
              >
                {initials}
              </div>
              <div>
                <div style={{fontSize: 13, fontWeight: 600, color: '#1e293b', lineHeight: 1.2}}>
                  {currentUser?.first_name} {currentUser?.last_name}
                </div>
                <div style={{fontSize: 12, color: '#94a3b8'}}>
                  {currentUser?.email}
                </div>
              </div>
            </div>
          </header>

          {/* Content */}
          <main style={{flex: 1, overflowY: 'auto', padding: '28px 28px'}}>
            <Outlet />
          </main>
        </div>
      </div>
    </PageDataProvider>
  )
}

export {MasterLayout}
